<template>
    <Drawer
        ref="drawer"
        title="Budget Entry"
        :loading="isLoading"
        size="is-large"
        @closing="reopenSource()"
        @closed="resetState()"
    >
        <template v-if="entry" #header>
            <div>
                <p v-if="entry.is_locked && entry.event?.lock_budget" class="px-4 py-1 bg-danger text-white">
                    <span style="font-weight: 600">{{ entry.event?.name }}</span>
                    budget is locked
                </p>
                <p v-else-if="entry.is_locked && isDepartmentLocked" class="px-4 py-1 bg-danger text-white">
                    Department
                    <span style="font-weight: 600">{{ entry.category?.department?.name }}</span>
                    is locked
                </p>
                <p v-else-if="entry.is_locked && !!entry.category?.is_locked" class="px-4 py-1 bg-danger text-white">
                    Subcategory
                    <span style="font-weight: 600">{{ entry.category?.name }}</span>
                    is locked
                </p>
                <p v-else-if="entry.lock_budget" class="px-4 py-1 bg-danger text-white">
                    This budget entry is locked and can't be edited
                </p>

                <p
                    v-if="entry.purchase_orders && entry.purchase_orders.length && !hasApprovedPOs"
                    class="px-4 py-1 bg-info text-white"
                >
                    This budget entry has an associated purchase order
                </p>
                <p
                    v-else-if="entry.purchase_orders && entry.purchase_orders.length && hasApprovedPOs"
                    class="px-4 py-1 bg-danger text-white"
                >
                    This budget entry has an approved purchase order
                </p>

                <p
                    v-if="entry.purchase_orders?.length && overbudget && warnBudget"
                    class="px-4 py-1 bg-danger text-white"
                >
                    This entry is currently over budget
                </p>
                <p
                    v-else-if="entry.purchase_orders?.length && !overbudget && warnBudget"
                    class="px-4 py-1 bg-warning text-white"
                >
                    This entry is currently over budget for unapproved purchase orders
                </p>
                <p v-if="!userCanEdit()" class="px-4 py-1 bg-info text-white">
                    You do not have permission to edit budget entries
                </p>
            </div>
            <div class="tw-flex tw-gap-3 h2 justify-content-between">
                <EditableText
                    v-model="entry.name"
                    :max-length="255"
                    :editable="isEditable"
                    @update:model-value="debouncedUpdateEntry()"
                />
                <div class="tw-flex tw-items-end tw-gap-2">
                    <Tooltip
                        v-if="Auth.can('view budget entries') && (entry.markup || entry.markup_source)"
                        position="left"
                        label="View Markup"
                    >
                        <Button type="is-warning" class="tw-float-right" @click="openMarkupEntry()">
                            <i class="mdi mdi-tag-arrow-up"></i>
                        </Button>
                    </Tooltip>
                    <LockItemControls
                        lockable-name="Budget Entry"
                        lockable-type="entry"
                        :lockable="entry"
                        class="tw-ml-auto"
                        :disabled="!!entry.category?.is_locked || !!entry.event?.lock_budget"
                        @toggle="refreshData()"
                    />
                </div>
            </div>
            <span class="tw-inline-flex tw-gap-2">
                <Tag v-if="entry.is_source_recharge" :status="entry.status" />
                <ApprovalControls
                    v-else
                    :approvables="[entry as Approvable]"
                    :locked="entry.is_locked || !isEditable || !isResetable || !entry.supplier_id"
                    @reload="debouncedUpdateEntry()"
                />
                <Button
                    v-if="isDeletable"
                    type="is-danger"
                    class="tw-flex tw-gap-2"
                    :disabled="isLoading"
                    @click="deleteEntry()"
                >
                    <i class="mdi mdi-delete"></i>
                    Delete
                </Button>
            </span>
        </template>
        <div v-if="entry" class="tw-h-full tw-flex tw-flex-col">
            <div class="tw-flex tw-items-start tw-mb-5">
                <FormField v-if="account.preferences.show_fixed_costs" class="tw-pr-5">
                    <InputEdit
                        v-model="entry.fixed_cost"
                        :label="'Fixed ' + (entry.entry_type === 1 ? 'Cost' : 'Revenue')"
                        size="is-small"
                        currency
                        :editable="isEditable"
                        :min-value="0"
                        @updated="updateEntry()"
                    />
                </FormField>
                <FormField class="tw-px-5">
                    <InputEdit
                        v-model="entry.budgeted_cost"
                        :label="'Budgeted ' + (entry.entry_type === 1 ? 'Cost' : 'Revenue')"
                        size="is-small"
                        currency
                        :editable="isEditable"
                        :min-value="0"
                        @updated="updateEntry()"
                    />
                </FormField>
                <FormField v-if="isEditable || markupExists" label="Markup" class="tw-px-5">
                    <Button
                        v-if="!markupExists && !entry.category?.is_revenue && isEditable"
                        type="is-primary"
                        class="tw-flex tw-gap-1 tw-items-center"
                        @click="
                            budgetmarkupmodal?.open({
                                donorEntry: entry,
                            })
                        "
                    >
                        <i class="mdi mdi-plus"></i>
                        Markup
                    </Button>
                    <div v-else-if="markupExists && entry.markup" class="tw-flex tw-flex-col tw-gap-1 tw-items-start">
                        <Button v-if="!isEditingMarkup" type="is-link" class="!tw-p-0" @click="isEditingMarkup = true">
                            {{ formatCurrency(entry.markup.budgeted_cost - entry.budgeted_cost, 2, eventCurrency) }}
                        </Button>
                        <PercentageValue
                            v-else-if="isEditingMarkup"
                            :model-value="entry.markup.budgeted_cost - entry.budgeted_cost"
                            :base-value="entry.budgeted_cost"
                            :loading="isSavingMarkup"
                            :min="0"
                            currency
                            @keypress.native.enter.prevent="saveMarkup()"
                            @update:model-value="(val) => (newMarkupValue = val ? Number(val) : 0)"
                        />
                        <Checkbox v-model="entry.markup.markup_sync_fields" @update:model-value="updateEntry()">
                            Also apply to fixed amount
                        </Checkbox>
                    </div>
                </FormField>
                <FormField
                    v-if="entry.user"
                    label="Created by"
                    class="tw-hidden xl:tw-block-block tw-mb-0 tw-pl-5 tw-ml-auto tw-text-right"
                >
                    <User :user="entry.user" size="small" :hide-name="false" />
                </FormField>
            </div>
            <div class="tw-flex tw-gap-3">
                <FormField class="tw-flex-1" label="Subcategory">
                    <Selector
                        v-model="entry.category_id"
                        :options="availableCategories"
                        :all-options="availableCategories"
                        :allow-clear="isEditable"
                        :editable="isEditable"
                        @update:model-value="updateEntry"
                    >
                        <template #optionLabel="{ option }">
                            {{ option.name }}{{ option.department ? ' (' + option.department.name + ')' : '' }}
                        </template>
                    </Selector>
                </FormField>
                <FormField class="tw-flex-1" :label="entry.entry_type === 1 ? 'Supplier' : 'Customer'">
                    <SupplierSelector
                        ref="supplierSelector"
                        :model-value="entry.supplier_id"
                        :editable="isEditable"
                        @update:model-value="handleSupplierChange"
                    >
                        <template #optionLabel="{ option }">
                            {{ option.name }}
                        </template>
                    </SupplierSelector>
                </FormField>
            </div>
            <Tabs
                v-model="defaultTab"
                :tabs="visibleTabs"
                container-class="tw-overflow-y-auto tw-flex-1"
                header-class="tw-mt-5"
                content-class="tw-flex-1 tw-h-full"
            >
                <template #tab(activity)>
                    <DrawerCommentsTab
                        :activity="entry.activity"
                        :comments="entry.comments"
                        :disabled="!isEditable"
                        :loading="isLoading"
                        allow-external
                        @comment="upsertComment"
                        @external-comment="(val: string) => upsertComment(val, true)"
                        @update-comment="upsertComment"
                    />
                </template>
                <template #tab(files)>
                    <DrawerFilesTab
                        class="tw-mt-5 tw-mx-3"
                        :endpoint="
                            route('api.account.event.entries.files.upload', {
                                entry: entry.id,
                                event: entry.event_id,
                            })
                        "
                        :complete-endpoint="
                            route('api.account.event.entries.files.completeUpload', {
                                entry: entry.id,
                                event: entry.event_id,
                            })
                        "
                        :files="entry.files"
                        :disabled="!isEditable"
                        @updated="refreshData()"
                        @uploaded="capturePosthogEvent('budget_file_uploaded')"
                    />
                </template>
                <template #tab(purchase_orders)>
                    <PurchaseOrdersTab
                        :entry
                        :disabled="!Auth.can('update budget entries') || entry.event?.status === 'Closed'"
                        :purchase-orders="entry.purchase_orders ? entry.purchase_orders : []"
                        @opened="closeModal()"
                    />
                </template>
                <template v-if="store.get('account.features.scenarios')" #tab(adjustments)>
                    <DrawerAdjustmentTab ref="adjustmentsTab" :entry />
                </template>
                <template #tab(revenue)>
                    <RevenueEntryTab
                        v-if="revenueEntries"
                        :entry
                        :revenue-entries="revenueEntries"
                        :disabled="!isEditable"
                        @opened="closeModal()"
                    />
                </template>
                <template v-if="isAdmin" #tab(audits)>
                    <Audits
                        v-if="entry && defaultTab === 'audits'"
                        model="App\Models\BudgetEntry"
                        :model-id="entry.id"
                    />
                </template>
            </Tabs>
        </div>
        <template v-if="entry" #footer>
            <PresenceIndicator :channel="'BudgetEntry.' + entry.id" label="Currently viewing this Budget Entry" />
        </template>
        <BudgetMarkup v-if="entry" ref="budgetmarkupmodal" @created="refreshData()" />
    </Drawer>
</template>
<script setup lang="ts">
import debounce from 'lodash/debounce';
import Drawer from '@/js/components/Drawer.vue';
import ApprovalControls from '@/js/components/widgets/ApprovalControls.vue';
import LockItemControls from '@/js/components/widgets/LockItemControls.vue';
import Selector from '@/js/components/controls/Selector.vue';
import SupplierSelector from '@/js/components/controls/SupplierSelector.vue';
import { useDataStore } from '@/js/stores/DataStore.js';
import EditableText from '@/js/components/controls/EditableText.vue';
import PurchaseOrdersTab from '@/js/components/drawers/BudgetEntry/PurchaseOrdersTab.vue';
import RevenueEntryTab from '@/js/components/drawers/BudgetEntry/RevenueEntryTab.vue';
import Button from '@/js/components/controls/Button.vue';
import BudgetMarkup from '@/js/components/modals/Budgeting/BudgetMarkup.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import PresenceIndicator from '@/js/components/widgets/PresenceIndicator.vue';
import Tabs from '@/js/components/widgets/Tabs.vue';
import User from '@/js/components/widgets/User.vue';
import Checkbox from '@/js/components/controls/Checkbox.vue';
import PercentageValue from '@/js/components/widgets/PercentageValue.vue';
import DrawerAdjustmentTab from '@/assets/js/eventbooks/components/drawer-tabs/adjustment.vue';
import DrawerFilesTab from '@/assets/js/eventbooks/components/drawer-tabs/files.vue';
import DrawerCommentsTab from '@/assets/js/eventbooks/components/drawer-tabs/comments.vue';
import Tooltip from '@/js/components/Tooltip.vue';
import { toast, formatCurrency } from '@/js/utils.js';
import { sockets } from '@/js/composables/useSockets.js';
import Tag from '@/js/components/Tag.vue';
import { useBudgetEntryDrawer } from '@/js/composables/useBudgetEntryDrawer.js';
import InputEdit from '@/assets/js/eventbooks/components/input-edit.vue';
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
import { Eventbus, type BudgetEntrySource } from '@/js/utils/eventbus.ts';
import type { BudgetEntry } from '@/js/types/budgetEntry.ts';
import { axios } from '@/js/utils/axios.ts';
import { route } from '@/js/utils.js';
import type { PurchaseOrder } from '@/js/types/purchaseOrder.ts';
import { Auth } from '@/js/utils.js';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';
import type { Approvable } from '@/js/types/approvable.ts';
import { alert, confirm } from '@/js/composables/useModals.ts';
import Audits from '@/js/components/pages/Admin/Tables/Audits.vue';

const { ready } = useBudgetEntryDrawer();

const store = useDataStore();
const account = store.get('account');
const isLoading = ref(false);
const supplierSelector = useTemplateRef('supplierSelector');
const adjustmentsTab = useTemplateRef('adjustmentsTab');
const isEditingMarkup = ref(false);
const isSavingMarkup = ref(false);
const entry = ref<BudgetEntry | null>(null);
const overbudget = ref(false);
const warnBudget = ref(false);
const availableCategories = ref([]);
const newMarkupValue = ref<number>(0);
const source = ref<BudgetEntrySource | null>(null);
const budgetmarkupmodal = useTemplateRef('budgetmarkupmodal');
const revenueEntries = ref(null);
const socketChannel = ref<string | null>(null);
const defaultTab = ref('activity');
const drawer = useTemplateRef('drawer');
const isAdmin = computed(() => store.get('current_user.is_admin') === true);

const isEditable = computed(() => {
    return (
        Auth.can('update budget entries') &&
        !entry.value?.is_locked &&
        entry.value?.event?.status !== 'Closed' &&
        !entry.value?.is_source_recharge
    );
});
const markupExists = computed(() => {
    return !!entry.value?.markup;
});
const isResetable = computed(() => {
    return (
        Object.values(entry.value?.purchase_orders ?? []).filter((po) =>
            ['Complete', 'Sent', 'Approved'].includes(po.status)
        ).length === 0 ||
        ['Draft', 'Awaiting Approval', 'Awaiting Reapproval', 'Rejected'].includes(entry.value?.status ?? '')
    );
});
const isDeletable = computed(() => {
    return (
        Auth.can('delete budget entries') &&
        !entry.value?.is_locked &&
        entry.value?.event?.status !== 'Closed' &&
        !hasApprovedPOs.value
    );
});
const hasApprovedPOs = computed(() => {
    return !(
        Object.values(entry.value?.purchase_orders ?? []).filter((po) =>
            ['Complete', 'Sent', 'Approved'].includes(po.status)
        ).length === 0
    );
});
const eventCurrency = computed(() => {
    return entry.value?.event?.currency_code;
});
const isDepartmentLocked = computed(() => {
    return (
        entry.value?.category?.department?.total_locked_categories ===
        entry.value?.category?.department?.total_categories
    );
});
const visibleTabs = computed(() => {
    const tabs = [
        { title: 'Activity', name: 'activity' },
        { title: 'Files', name: 'files' },
    ];
    if (entry.value && entry.value.entry_type !== 2) {
        tabs.push({ title: 'Purchase Orders', name: 'purchase_orders' });
    }
    if (store.get('account.features.scenarios')) {
        tabs.push({ title: 'Adjustments', name: 'adjustments' });
    }
    if (entry.value && entry.value.entry_type === 2 && store.get('current_event')) {
        tabs.push({ title: 'Revenue', name: 'revenue' });
    }
    if (isAdmin.value) {
        tabs.push({ title: 'Audits', name: 'audits' });
    }
    return tabs;
});

watch(entry, () => {
    handleSocketChannel();
});

onMounted(() => {
    Eventbus.$on('budget:entry', (payload) => {
        defaultTab.value = payload.defaultTab ?? 'activity';
        if (!Auth.can('view budget entries')) {
            closeModal();
        }
        if (payload.source) {
            source.value = payload.source;
        }
        resetState();
        refreshData(payload.entry, payload.event);
        if (drawer.value) {
            drawer.value.open();
        }
    });
    Eventbus.$on('close:BudgetEntry', () => {
        closeModal();
    });
    Eventbus.$on('refresh:BudgetEntry', () => {
        refreshData();
    });
    ready.value = true;
});

const handleSocketChannel = () => {
    const channelName = entry.value ? `BudgetEntry.${entry.value.id}` : null;
    if (channelName === null && socketChannel.value) {
        sockets.leaveChannel(socketChannel.value);
        socketChannel.value = null;
        return;
    }
    if (socketChannel.value === channelName) {
        return;
    }
    if (socketChannel.value !== channelName) {
        sockets.leaveChannel(socketChannel);
    }
    socketChannel.value = channelName;
    sockets.joinChannel(socketChannel.value).listen('.reload_purchase_orders', () => {
        if (!entry.value) {
            return;
        }
        refreshData();
    });
};
const closeModal = () => {
    resetState();
    drawer.value?.close();
};
const reopenSource = () => {
    if (source.value) {
        Eventbus.$emit(source.value.emitter, source.value.payload);
        source.value = null;
    }
};
const userCanEdit = () => {
    return entry.value !== null && Auth.can('update budget entries') === true;
};
const loadEntry = async (entryId: number, eventId: number | null = null) => {
    const { data } = await axios.get(
        route('api.account.event.entries.get', {
            event: eventId ?? entry.value?.event_id,
            entry: entryId,
        })
    );
    entry.value = data as BudgetEntry;
    overbudgetCheck();
};
const refreshData = async (entryId: number | null = null, eventId: number | null = null) => {
    entryId = entryId ?? entry.value?.id ?? null;
    eventId = eventId ?? entry.value?.event_id ?? null;
    if (!entryId) {
        return;
    }
    isLoading.value = true;
    try {
        await loadEntry(entryId, eventId);
        const isRevenue = entry.value?.category?.stream_type !== null;
        const { data: categoriesResponse } = await axios.get(
            route('api.account.event.categories', {
                event: entry.value?.event_id,
                is_revenue: isRevenue ? 1 : 0,
                include_tickets: 0,
            })
        );
        availableCategories.value = categoriesResponse;
        if (supplierSelector.value) {
            supplierSelector.value.reloadSuppliers();
        }
        handleSocketChannel();
        setTimeout(() => {
            if (adjustmentsTab.value) {
                adjustmentsTab.value.fetchAdjustments();
            }
        }, 50);
        if (entry.value?.entry_type === 2 && entry.value.category?.department?.type === 2) {
            // If this is a Revenue Budget Entry get the Revenue Entries
            const { data } = await axios.get(
                route('api.account.event.departments.revenueEntries', {
                    event: entry.value?.event_id,
                    department: entry.value?.category?.department?.id,
                })
            );
            revenueEntries.value = data;
        }
    } finally {
        isLoading.value = false;
    }
};
const refreshCommentData = async () => {
    if (!entry.value) {
        return;
    }
    isLoading.value = true;
    try {
        await loadEntry(entry.value.id, entry.value.event_id);
    } finally {
        isLoading.value = false;
    }
};
const deleteEntry = async () => {
    if (!entry.value) {
        return;
    }
    const currentEntry = entry.value;
    isLoading.value = true;
    const isRevenue = !store.get(`current_event.cost_departments.${currentEntry.category?.department_id}`);

    const confirmed = await confirm({
        title: 'Are you sure?',
        message: `Deleting this budget entry will delete any associated ${isRevenue ? 'revenue entries' : 'purchase orders'}.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        confirmType: 'is-danger',
    });
    if (!confirmed) {
        isLoading.value = false;
        return;
    }
    if (currentEntry.markup) {
        await alert({
            title: 'Associated Markup Entry Detected',
            message: `This budget entry has an associated markup entry and will be deleted as well.`,
        });
    }
    // delete method checks for and deleted any associated purchase orders and revenue entries, so we don't need to do anything extra here
    axios
        .delete(
            route('api.account.event.entries.delete', {
                event: currentEntry.event_id,
                entry: currentEntry.id,
            })
        )
        .then(() => {
            capturePosthogEvent('budget_entry_deleted');
            closeModal();
            toast('Success', 'Budget entry deleted', 'success');
            if (currentEntry.markup) {
                deleteMarkupEntry();
            }
        })
        .finally(() => (isLoading.value = false));
};
const deleteMarkupEntry = async () => {
    if (!entry.value?.markup) {
        return;
    }
    const markup = entry.value.markup;
    isLoading.value = true;
    axios
        .delete(
            route('api.account.event.entries.delete', {
                event: markup.event_id,
                entry: markup.id,
            })
        )
        .then(() => {
            capturePosthogEvent('budget_entry_deleted');
            toast('Success', 'Markup entry deleted', 'success');
            refreshData();
        })
        .finally(() => (isLoading.value = false));
};
const updateEntry = async () => {
    // updates not allowed if overbudget and no unapproved POs
    // This function is not called by add/update comments
    if ((overbudget.value && warnBudget.value) || !entry.value?.category_id || isLoading.value) {
        return;
    }
    const formData = entry.value;
    delete formData.activity;
    delete formData.comments;

    isLoading.value = true;
    try {
        const { data } = await axios.post(
            route('api.account.event.entries.update', {
                event: entry.value.event_id,
                entry: entry.value.id,
            }),
            formData
        );
        entry.value = data;
        capturePosthogEvent('budget_entry_updated');
        Eventbus.$emit('reload:entries');
    } finally {
        isLoading.value = false;
    }
};
const debouncedUpdateEntry = debounce(function () {
    updateEntry();
}, 3000);
const upsertComment = async (comment: string | { id: string }, external = false) => {
    if (!entry.value) {
        return;
    }
    isLoading.value = true;
    try {
        if (typeof comment === 'string') {
            if (!comment) {
                return;
            }
            await axios.post(
                route('api.account.event.entries.comment', {
                    event: entry.value.event_id,
                    entry: entry.value.id,
                }),
                {
                    text: comment,
                    external,
                }
            );
        } else {
            await axios.patch(
                route('api.account.event.comments.update', {
                    event: entry.value.event_id,
                    comment: comment.id,
                }),
                comment
            );
        }
        await refreshCommentData();
        Eventbus.$emit('reload:entries');
    } finally {
        isLoading.value = false;
    }
};
const resetState = () => {
    entry.value = null;
    overbudget.value = false;
    warnBudget.value = false;
    isLoading.value = false;
    isSavingMarkup.value = false;
    availableCategories.value = [];
    source.value = null;
    revenueEntries.value = null;
};
const overbudgetCheck = () => {
    if (!entry.value || !entry.value.purchase_orders) {
        return;
    }
    const approvedSubtotal = entry.value.purchase_orders
        .filter((po: PurchaseOrder) => po.status !== 'Void' && po.uncommitted)
        .reduce((sum, po) => sum + po.subtotal, 0);
    const unApprovedSubtotal = entry.value.purchase_orders
        .filter((po: PurchaseOrder) => po.status !== 'Void' && !po.uncommitted)
        .reduce((sum, po) => sum + po.subtotal, 0);
    // In order to do comparison, convert everything to integer numbers
    const poTotal = parseFloat(String(approvedSubtotal)) + parseFloat(String(unApprovedSubtotal));

    // Test overall Budget
    if (Math.round(poTotal * 100) <= Math.round(entry.value.budgeted_cost * 100)) {
        overbudget.value = false;
        warnBudget.value = false;
        // clear all warnings
    }
    // Overbudget conditions
    // overbudget with the unapproved Items
    else if (Math.round((entry.value.budgeted_cost - approvedSubtotal) * 100) < Math.round(unApprovedSubtotal * 100)) {
        overbudget.value = false;
        warnBudget.value = true;
        // Go and void all the pending/created Purchase Orders
    } else if (Math.round(entry.value.budgeted_cost * 100) < Math.round(approvedSubtotal * 100)) {
        overbudget.value = true;
        warnBudget.value = false;
        //Do not allow/save this change Hide the save button
    } else {
        // Over budget for approved items AND unapproved items
        overbudget.value = true;
        warnBudget.value = true;
    }
};
const openMarkupEntry = () => {
    if (!entry.value) {
        return;
    }
    // Fetches the markup pair matching BE and refreshes the drawer with the details
    if (entry.value.markup) {
        Eventbus.$emit('budget:entry', {
            event: entry.value.markup.event_id,
            entry: entry.value.markup.id,
            scenarioId: store.get('current_scenario.id'),
        });
    } else if (entry.value.markup_source) {
        Eventbus.$emit('budget:entry', {
            event: entry.value.markup_source.event_id,
            entry: entry.value.markup_source.id,
            scenarioId: store.get('current_scenario.id'),
        });
    }
};
const saveMarkup = () => {
    if (!entry.value) {
        return;
    }
    if (!entry.value.markup || isNaN(newMarkupValue.value)) {
        return;
    }
    isSavingMarkup.value = true;
    entry.value.markup.budgeted_cost =
        parseFloat(String(entry.value.budgeted_cost)) + parseFloat(String(newMarkupValue.value));
    // Work out the percentage difference between the new markup and the original budgeted cost and
    // also update fixed cost to reflect the new markup
    const percentage = entry.value.markup.budgeted_cost / (entry.value.budgeted_cost ? entry.value.budgeted_cost : 100);

    axios
        .patch(
            route('api.account.event.entries.update-markup', {
                event: entry.value.event_id,
                entry: entry.value.markup.id,
            }),
            {
                budgeted_cost: entry.value.markup.budgeted_cost,
                fixed_cost: Number(entry.value.fixed_cost) * (entry.value.markup.markup_sync_fields ? percentage : 1),
            }
        )
        .then(() => {
            capturePosthogEvent('budget_entry_updated');
            toast('Markup Entry Updated', 'The marked up entry has been updated successfully', 'success');
        })
        .finally(() => {
            isSavingMarkup.value = false;
        });

    isEditingMarkup.value = false;
};
const handleSupplierChange = async (val: null | number | undefined) => {
    if (!entry.value) {
        return;
    }
    const previousSupplierId = entry.value.supplier_id;

    if (previousSupplierId === val) {
        return;
    }

    const draftPos = (entry.value.purchase_orders ?? []).filter((po) => ['Draft'].includes(po.status));

    if (draftPos.length) {
        const confirmed = await confirm({
            title: 'Change Supplier',
            message: `Changing the supplier will update ${draftPos.length} DRAFT associated purchase orders to the new supplier. Are you sure you want to change the supplier?`,
            confirmText: 'Yes, change supplier',
            cancelText: 'No, keep current supplier',
            confirmType: 'is-warning',
        });

        if (!confirmed) {
            entry.value.supplier_id = previousSupplierId;
            if (supplierSelector.value && supplierSelector.value.selector) {
                supplierSelector.value.selector.selectorValue = previousSupplierId;
            }
            return;
        }
    }

    entry.value.supplier_id = val ?? null;
    updateEntry();
};
</script>
